import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, Briefcase, Building2 } from "lucide-react";
import { api } from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import { useAuth } from "@/context/AuthContext";

const INDUSTRIES = ["All", "Technology", "Finance", "Healthcare", "Fashion", "Manufacturing", "Real Estate", "Hospitality", "Education", "Marketing", "Operations"];

export default function Directory() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [q, setQ] = useState("");
  const [industry, setIndustry] = useState("All");
  const [city, setCity] = useState("");
  const [chapter, setChapter] = useState("");

  const fetchMembers = () => {
    api.get("/members", {
      params: {
        q: q || undefined,
        industry: industry === "All" ? undefined : industry,
        city: city || undefined,
        chapter: chapter || undefined,
      },
    }).then((r) => setMembers(r.data)).catch(() => {});
  };

  useEffect(() => { fetchMembers(); api.get("/chapters").then((r) => setChapters(r.data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(fetchMembers, 250); return () => clearTimeout(t); // eslint-disable-next-line
  }, [q, industry, city, chapter]);

  const cities = [...new Set(members.map((m) => m.city).filter(Boolean))];

  return (
    <DashboardShell role={user?.role === "admin" ? "admin" : "member"} title="Member Directory" subtitle="Search the WOWSHE community">
      <div data-testid="directory-page">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-8">
          <div className="lg:col-span-7">
            <p className="text-sm text-plumSoft font-light">{members.length} members visible. Active member profiles only.</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-aubergine/8 p-6 grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-4 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-plum" />
            <input
              data-testid="dir-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name or business..."
              className="input-luxe pl-11"
            />
          </div>
          <select data-testid="dir-industry" value={industry} onChange={(e) => setIndustry(e.target.value)} className="input-luxe md:col-span-3">
            {INDUSTRIES.map((i) => <option key={i}>{i}</option>)}
          </select>
          <select data-testid="dir-city" value={city} onChange={(e) => setCity(e.target.value)} className="input-luxe md:col-span-2">
            <option value="">All cities</option>
            {cities.map((c) => <option key={c}>{c}</option>)}
          </select>
          <select data-testid="dir-chapter" value={chapter} onChange={(e) => setChapter(e.target.value)} className="input-luxe md:col-span-3">
            <option value="">All chapters</option>
            {chapters.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
          {members.map((m) => (
            <Link to={`/members/${m.id}`} key={m.id} data-testid={`member-card-${m.id}`} className="card-luxe overflow-hidden group block">
              <div className="aspect-[5/4] overflow-hidden bg-lavender">
                {m.avatar ? (
                  <img src={m.avatar} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-plum font-display text-5xl">
                    {m.name.charAt(0)}
                  </div>
                )}
              </div>
              <div className="p-6">
                <h3 className="font-display text-2xl text-aubergine">{m.name}</h3>
                <p className="text-sm text-plumSoft mt-1">{m.business || "—"}</p>
                <div className="mt-5 space-y-2 text-xs text-plum">
                  {m.industry && <div className="flex items-center gap-2"><Briefcase className="w-3.5 h-3.5" /><span>{m.industry}</span></div>}
                  {m.city && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /><span>{m.city}</span></div>}
                  {m.chapter && <div className="flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /><span>{m.chapter}</span></div>}
                </div>
                {m.bio && <p className="mt-5 text-sm text-plumSoft font-light leading-relaxed line-clamp-3">{m.bio}</p>}
                <p className="mt-5 text-xs text-plum link-underline">View profile →</p>
              </div>
            </Link>
          ))}
          {members.length === 0 && (
            <div className="md:col-span-3 text-center py-20 text-plumSoft">
              No members match your filters. Try widening the search.
            </div>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
